/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { NotEmptyValidator } from '@squiz/dxp-console-support';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DEFAULT,
  Button,
  FORM_THEME_LITE,
  Form,
  TextField,
  TextFieldProps,
  WIZARD_DIRECTION_NEXT,
  WizardStepProps,
} from '@squiz/sds';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CORE_STRINGS } from '../../../../Constants/Strings.constants';
import { TENANT_SETTINGS_PERMISSION, TenantSettingsServiceGetSettingDto } from '../../../../Services/tenant-settings';
import { SETTING_LIST_DATA_IDS, SETTING_LIST_FORM_IDS } from '../../SettingList.constants';
import { SelectSchema } from '../../elements/SelectSchema/SelectSchema';
import { SCHEMA_SELECT_FORM_IDS } from '../../elements/SelectSchema/SelectSchema.constants';
import { SelectSchemaProps } from '../../elements/SelectSchema/SelectSchema.props';
import { SelectService } from '../../elements/SelectService/SelectService';
import { SERVICE_SELECT_FORM_IDS } from '../../elements/SelectService/SelectService.constants';
import { SelectServiceProps } from '../../elements/SelectService/SelectService.props';
import { SettingCreateProps } from '../../elements/SettingCreate/SettingCreate.props';
import { checkForExistingSettingInService } from '../../utils/CheckForExistingSettingInService/CheckForExistingSettingInService';
import { SettingKeyValidator } from '../../utils/SettingKeyValidator/SettingKeyValidator';
import { SettingValueFormProps } from '../SettingValueForm/SettingValueForm.props';

/**
 * The form for creating a new setting.
 */
export const SettingCreateForm = (props: WizardStepProps): JSX.Element => {
  const { t } = useTranslation();
  const { close, setAfterClose, setButtonKeys, setButtons, updateStep } = props;

  const settingCreateProps = props.customProps as SettingCreateProps;
  const { clearCache, keyName, schemas, serviceId } = settingCreateProps;

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [formError, setFormError] = useState<string>(``);
  const [isFormSubmitted, setSubmit] = useState<boolean>(false);
  const [isBusy, setIsBusy] = useState<boolean>(false);

  // Form refs
  const settingKeyRef = useRef<HTMLInputElement>(null);
  const serviceRef = useRef<HTMLInputElement>(null);
  const schemaRef = useRef<HTMLInputElement>(null);

  // Validators
  const notEmptyValidator = useMemo(() => {
    return new NotEmptyValidator<boolean>((): boolean => false);
  }, []);

  const settingKeyValidator = useMemo(() => {
    return new SettingKeyValidator<boolean>((): boolean => false);
  }, []);

  // Labels
  const labels = useMemo(() => {
    return {
      cancelButton: t(CORE_STRINGS.cancel),
      invalidSettingKey: t(CORE_STRINGS.invalidSettingKey),
      next: t(CORE_STRINGS.next),
      requiredField: t(CORE_STRINGS.requiredField),
      settingExists: t(CORE_STRINGS.settingExists),
      settingKey: t(CORE_STRINGS.settingKey),
      settingKeyPlaceholder: t(CORE_STRINGS.settingKeyPlaceholder),
      title: t(CORE_STRINGS.createSetting),
    };
  }, [t]);

  const validateFields = useCallback(
    (inputs: Array<HTMLInputElement>): void => {
      const nextErrors = { ...fieldErrors };
      inputs.forEach((input: HTMLInputElement): void => {
        const fieldId = input.getAttribute(`id`) as string;
        delete nextErrors[fieldId];

        if (fieldId === SETTING_LIST_FORM_IDS.settingKey) {
          if (!notEmptyValidator.validate(input.value)) {
            nextErrors[fieldId] = labels.requiredField;
          } else if (!settingKeyValidator.validate(input.value)) {
            nextErrors[fieldId] = labels.invalidSettingKey;
          }
        }

        if (fieldId === SERVICE_SELECT_FORM_IDS.service && !notEmptyValidator.validate(input.value)) {
          nextErrors[fieldId] = labels.requiredField;
        }

        if (fieldId === SCHEMA_SELECT_FORM_IDS.schema && !notEmptyValidator.validate(input.value)) {
          nextErrors[fieldId] = labels.requiredField;
        }
      });
      setFieldErrors(nextErrors);
    },
    [fieldErrors, labels, notEmptyValidator, setFieldErrors, settingKeyValidator],
  );

  const errorChecker = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      validateFields([event.target]);
    },
    [validateFields],
  );

  const clearSelectError = (fieldId: string): void => {
    const nextErrors = { ...fieldErrors };
    delete nextErrors[fieldId];
    setFieldErrors(nextErrors);
  };

  const submitHandler = useCallback(async (): Promise<void> => {
    setIsBusy(true);
    setFormError('');

    // Set up inputs
    const inputs = {
      schema: schemaRef.current as HTMLInputElement,
      service: serviceRef.current as HTMLInputElement,
      settingKey: settingKeyRef.current as HTMLInputElement,
    };

    // Collect the current values of the form
    const settingKey = inputs.settingKey.value;
    const serviceId = inputs.service.value;
    const schemaId = inputs.schema.value;

    if (
      !settingKeyValidator.validate(settingKey) ||
      !notEmptyValidator.validate(serviceId) ||
      !notEmptyValidator.validate(schemaId)
    ) {
      const inputsToCheck = [inputs.settingKey, inputs.service, inputs.schema];
      validateFields(inputsToCheck);
      setSubmit(false);
      setIsBusy(false);
      return;
    }

    const dto: TenantSettingsServiceGetSettingDto = {
      schemaId,
      serviceId,
      settingKey,
    };

    const existingSetting = await checkForExistingSettingInService(dto);

    if (existingSetting) {
      setIsBusy(false);
      setSubmit(false);
      setFormError(labels.settingExists);
      return;
    }

    const nextProps: SettingValueFormProps = {
      ...dto,
      clearCache,
    };

    updateStep(WIZARD_DIRECTION_NEXT, nextProps);
  }, [clearCache, labels, notEmptyValidator, settingKeyValidator, updateStep, validateFields]);

  useEffect(() => {
    const cancelButton = (
      <Button
        data-id={SETTING_LIST_DATA_IDS.actionCreateSettingCancel}
        disabled={isBusy}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancelButton}
        theme={BUTTON_THEME_DEFAULT}
      />
    );
    const nextButton = (
      <Button
        data-id={SETTING_LIST_DATA_IDS.actionCreateSettingNext}
        disabled={isBusy}
        isLoading={isFormSubmitted}
        onClick={(): void => setSubmit(true)}
        text={labels.next}
      />
    );

    // Form submission check
    if (isFormSubmitted) {
      setButtonKeys([`next`]);
      setButtons([nextButton]);

      if (!isBusy) {
        submitHandler();
      }
    } else {
      setButtonKeys([`cancel`, `next`]);
      setButtons([cancelButton, nextButton]);
    }
  }, [close, isBusy, isFormSubmitted, labels, setAfterClose, setButtonKeys, setButtons, setSubmit, submitHandler]);

  // Setting key
  const settingKeyPlaceholder = keyName
    ? labels.settingKeyPlaceholder.replace(/{{settingKey}}/, keyName.toLowerCase())
    : labels.settingKeyPlaceholder.replace(/{{settingKey}}/, labels.settingKey.toLowerCase());
  const settingKeyFieldProps: TextFieldProps = {
    errorMessage: fieldErrors[SETTING_LIST_FORM_IDS.settingKey] || null,
    fieldLabel: keyName ?? labels.settingKey,
    id: SETTING_LIST_FORM_IDS.settingKey,
    name: SETTING_LIST_FORM_IDS.settingKey,
    onChange: (event: ChangeEvent<HTMLInputElement>): void => {
      errorChecker(event);
    },
    placeholder: settingKeyPlaceholder,
    required: true,
    value: '',
  };

  // Service
  const serviceFieldProps: SelectServiceProps = {
    callback: (): void => {
      clearSelectError(SERVICE_SELECT_FORM_IDS.service);
    },
    errorMessage: fieldErrors[SERVICE_SELECT_FORM_IDS.service] || null,
    permission: TENANT_SETTINGS_PERMISSION.CREATE,
  };

  // Schema
  const schemaFieldProps: SelectSchemaProps = {
    callback: (): void => {
      clearSelectError(SCHEMA_SELECT_FORM_IDS.schema);
    },
    errorMessage: fieldErrors[SCHEMA_SELECT_FORM_IDS.schema] || null,
    schemas: schemas,
    uiAccess: `create`,
  };

  const getServiceDropdown = (): JSX.Element => {
    if (serviceId) {
      return <input id={SERVICE_SELECT_FORM_IDS.service} ref={serviceRef} type="hidden" value={serviceId} />;
    }
    return <SelectService {...serviceFieldProps} ref={serviceRef} />;
  };

  const getSchemaDropdown = (): JSX.Element => {
    if (schemas.length === 1) {
      const schemaId = schemas[0].schemaId;
      return <input id={SCHEMA_SELECT_FORM_IDS.schema} ref={schemaRef} type="hidden" value={schemaId} />;
    }

    return <SelectSchema {...schemaFieldProps} ref={schemaRef} />;
  };

  return (
    <div className="service-settings__form" data-id={SETTING_LIST_DATA_IDS.settingCreateForm}>
      <Form errorMessage={formError} legend={labels.title} theme={FORM_THEME_LITE}>
        <TextField {...settingKeyFieldProps} data-id={SETTING_LIST_DATA_IDS.settingKey} ref={settingKeyRef} />
        {getServiceDropdown()}
        {getSchemaDropdown()}
      </Form>
    </div>
  );
};
